/**
 * Typisierte Hooks für `domains/entities` — den Baum und den Kopf jedes
 * Objekts, artübergreifend (Spec 02a, „Endpunkte").
 *
 * `fetch` steht ausschließlich hier — nie in einer Komponente
 * (`apps/web/CLAUDE.md`). Query-Keys kommen ausschließlich aus
 * `lib/query-keys.ts`.
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

import type { ArchivedEntityKind } from '@/lib/archived-notice'
import type { components } from '@/lib/api-types'
import { request } from '@/lib/api'
import { keys } from '@/lib/query-keys'
import { berlinEndOfDayIso, todayInBerlin } from '@/lib/date'

export type TreeNode = components['schemas']['TreeNode']
export type ObjectHead = components['schemas']['ObjectHead']
export type MoveTarget = components['schemas']['MoveTarget']
/** Spec 05b — Rumpf von `POST /entities/private-areas`. */
export type PrivateAreaCreate = components['schemas']['PrivateAreaCreate']
/** Spec 05b — Rumpf von `PATCH /entities/private-areas/{id}`. */
export type PrivateAreaRename = components['schemas']['PrivateAreaRename']

export interface TreePage {
  readonly items: TreeNode[]
  readonly next_cursor: string | null
}

interface UseEntitiesTreeOptions {
  /** `false` für eine Kinderebene, solange sie nicht aufgeklappt ist — der
   * Baum lädt eine Ebene erst beim ersten Aufklappen (Spec 02a, „Der Baum"). */
  readonly enabled?: boolean
}

/**
 * Eine Ebene des Baums. `parentId` fehlt für die Wurzeln.
 *
 * Wie beim Grundstücks-Baum in Feature 01 zeigt jede Ebene eine Seite
 * (Vorgabe: 50) ohne „mehr laden" — für ~10 interne Nutzer kein reales
 * Limit in dieser Ausbaustufe.
 */
export function useEntitiesTree(parentId: string | undefined, options: UseEntitiesTreeOptions = {}) {
  return useQuery({
    queryKey: keys.entities.list({ parentId: parentId ?? null }),
    queryFn: ({ signal }) => {
      const query = parentId !== undefined ? `?parent_id=${parentId}` : ''
      return request<TreePage>(`/entities/tree${query}`, { signal })
    },
    enabled: options.enabled ?? true,
  })
}

interface FlatEntitiesFilter {
  readonly kind?: string
  readonly within?: string
  readonly q?: string
  readonly limit?: number
}

interface UseFlatEntitiesOptions {
  readonly enabled?: boolean
}

/** Die flache, cursorbasierte Objektmenge aus Spec 14b. Der Filter ist Teil
 * des bestehenden Entities-Keys, damit ein Scopewechsel weder Cursor noch
 * verspätete Seiten der vorherigen Menge übernehmen kann. */
export function useFlatEntities(filter: FlatEntitiesFilter, options: UseFlatEntitiesOptions = {}) {
  return useInfiniteQuery({
    queryKey: keys.entities.list({
      mode: 'flat',
      kind: filter.kind,
      within: filter.within,
      q: filter.q,
      limit: filter.limit,
    }),
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams({ mode: 'flat' })
      if (filter.kind !== undefined) params.set('kind', filter.kind)
      if (filter.within !== undefined) params.set('within', filter.within)
      if (filter.q !== undefined && filter.q !== '') params.set('q', filter.q)
      if (filter.limit !== undefined) params.set('limit', String(filter.limit))
      if (pageParam !== null) params.set('cursor', pageParam)
      return request<TreePage>('/entities/tree?' + params.toString(), { signal })
    },
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor,
    enabled: options.enabled ?? true,
  })
}

interface UseEntityHeadOptions {
  readonly enabled?: boolean
}

/** Der Rahmen des Bildschirms *Objekt* — Titel, Pfad, Archivierungszustand,
 * für jede Art gleich (Spec 02a, „Antwort GET /entities/{id}"). */
export function useEntityHead(entityId: string, options: UseEntityHeadOptions = {}) {
  return useQuery({
    queryKey: keys.entities.detail(entityId),
    queryFn: ({ signal }) => request<ObjectHead>(`/entities/${entityId}`, { signal }),
    enabled: options.enabled ?? true,
  })
}

/** Sichtbare Objektprojektionen werden beim Zurückkehren und am nächsten
 * Berliner Kalendertag erneut gelesen. Es gibt kein Polling: Der Timer hat
 * genau einen Termin, wird beim Unmount entfernt und der bestehende
 * `entities`-Präfix trifft nur bereits geladene Projektionen. */
export function useEntityProjectionRefresh(enabled = true) {
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!enabled) return

    let timeout: ReturnType<typeof setTimeout> | undefined
    let day = todayInBerlin()
    const refresh = () => {
      // Sichtbarkeit und Fensterfokus können dieselbe Rückkehr melden.
      // Einen bereits laufenden Abruf gemeinsam nutzen statt ihn neu zu starten.
      void queryClient.invalidateQueries({ queryKey: keys.entities.all }, { cancelRefetch: false })
    }
    const scheduleNextDay = () => {
      const deadline = new Date(berlinEndOfDayIso(day)).getTime() + 1
      const delay = Math.max(1_000, deadline - Date.now())
      timeout = setTimeout(() => {
        const nextDay = todayInBerlin()
        if (nextDay !== day) {
          day = nextDay
          refresh()
        }
        scheduleNextDay()
      }, delay)
    }
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      const nextDay = todayInBerlin()
      if (nextDay !== day) day = nextDay
      refresh()
    }

    window.addEventListener('focus', onVisible)
    document.addEventListener('visibilitychange', onVisible)
    scheduleNextDay()
    return () => {
      window.removeEventListener('focus', onVisible)
      document.removeEventListener('visibilitychange', onVisible)
      if (timeout !== undefined) clearTimeout(timeout)
    }
  }, [enabled, queryClient])
}

/** `entity_kind` → das Pfadsegment der Art-eigenen Endpunkte. Reines Wissen
 * über REST-Pfade, keine Fachlogik — dieselbe Art Abbildung wie die
 * `<kind>-row-<id>`-Bezeichner im Baum. Seit Spec 03 um `tenant`/`contract`
 * erweitert — beide sind archivierbar und nehmen damit am Hinweisstreifen
 * (`object-archived-undo`) genauso teil wie die drei bestehenden Arten. */
const KIND_PATH: Record<ArchivedEntityKind, string> = {
  property: 'properties',
  building: 'buildings',
  unit: 'units',
  tenant: 'tenants',
  contract: 'contracts',
}

const KIND_KEYS_ALL: Record<ArchivedEntityKind, readonly unknown[]> = {
  property: keys.properties.all,
  building: keys.buildings.all,
  unit: keys.units.all,
  tenant: keys.tenants.all,
  contract: keys.contracts.all,
}

interface UnarchiveEntityArgs {
  readonly kind: ArchivedEntityKind
  readonly id: string
  readonly ifMatch: string
}

/**
 * Wiederherstellen über den Hinweisstreifen (`object-archived-undo`,
 * Spec 02a, „Archivieren"). Generisch über alle drei Arten — der Hinweis
 * kennt nur `id`, `kind` und `version` des zuletzt archivierten Objekts, kein
 * volles Detailobjekt.
 */
export function useUnarchiveEntity() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ kind, id, ifMatch }: UnarchiveEntityArgs) =>
      request(`/${KIND_PATH[kind]}/${id}/unarchive`, { method: 'POST', ifMatch }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
      void queryClient.invalidateQueries({ queryKey: KIND_KEYS_ALL[variables.kind] })
    },
  })
}

// ── Umhängen (Spec 02b, „Umhängen") ─────────────────────────────────────

interface MoveTargetsPage {
  readonly items: MoveTarget[]
  readonly next_cursor: string | null
}

interface UseMoveTargetsOptions {
  readonly q?: string
  readonly enabled?: boolean
}

/**
 * Die zulässigen Ziele fürs Umhängen (`GET /entities/{id}/move-targets`).
 * Der Server entscheidet allein, was zulässig ist — Art laut Hierarchietabelle,
 * Sichtbarkeit, Archivstatus, „nicht der Knoten selbst oder sein Nachfahre"
 * (I9). Der Client filtert die Liste nicht nach.
 */
export function useMoveTargets(entityId: string, options: UseMoveTargetsOptions = {}) {
  const q = options.q ?? ''

  return useQuery({
    queryKey: keys.entities.list({ moveTargetsFor: entityId, q }),
    queryFn: ({ signal }) => {
      const query = q.trim() === '' ? '' : `?q=${encodeURIComponent(q.trim())}`
      return request<MoveTargetsPage>(`/entities/${entityId}/move-targets${query}`, { signal })
    },
    enabled: options.enabled ?? true,
  })
}

interface MoveEntityArgs {
  readonly entityId: string
  readonly parentId: string
  readonly ifMatch: string
}

/**
 * `POST /entities/{id}/move`. Invalidiert `keys.entities.all` als Präfix,
 * nicht einzeln aufgezählte Keys — Baum, Kopf und Pfad hängen alle an
 * `parent_id`/`ancestor_ids`, die sich mit dem Umhängen ändern (Spec 02b,
 * „Oberfläche · Umhängen": „Pfad und Baum aktualisieren sich über die
 * Präfix-Invalidierung keys.entities.all").
 */
export function useMoveObject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ entityId, parentId, ifMatch }: MoveEntityArgs) =>
      request<ObjectHead>(`/entities/${entityId}/move`, {
        method: 'POST',
        body: { parent_id: parentId },
        idempotencyKey: crypto.randomUUID(),
        ifMatch,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

// ── Privater Bereich (Spec 05b) ──────────────────────────────────────────

interface SwitchVisibilityArgs {
  readonly entityId: string
  readonly visibility: 'private' | 'inherited'
  readonly ifMatch: string
}

/** `POST /entities/{id}/visibility` — schaltet um, `object-private`
 * (Spec 05b, „Umschalten"). Invalidiert `entities.all`: Kopf, Pfad und Baum
 * hängen alle an `visibility`/`ancestor_ids`, dieselbe Präfix-Invalidierung
 * wie `useMoveObject`. */
export function useSwitchVisibility() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ entityId, visibility, ifMatch }: SwitchVisibilityArgs) =>
      request<ObjectHead>(`/entities/${entityId}/visibility`, {
        method: 'POST',
        body: { visibility },
        idempotencyKey: crypto.randomUUID(),
        ifMatch,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

/** `POST /entities/private-areas` — der private Bereich als eigene Wurzel
 * (`private-area-add`, Spec 05b, „Der private Bereich als eigene Wurzel").
 * Kein optimistischer Baumeintrag: Anders als bei Grundstück/Gebäude/Einheit
 * gibt es hier kein „und noch eins" (Spec: „man legt einen privaten Bereich
 * an, nicht fünf") — der primäre Knopf bleibt synchron, wie beim ersten
 * Speichern in `PropertySheet`. */
export function useCreatePrivateArea() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (body: PrivateAreaCreate) =>
      request<ObjectHead>('/entities/private-areas', {
        method: 'POST',
        body,
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

interface RenamePrivateAreaArgs {
  readonly entityId: string
  readonly body: PrivateAreaRename
  readonly ifMatch: string
}

/** `PATCH /entities/private-areas/{id}` — Umbenennen, der einzige Reiter
 * *Stammdaten* eines Ordners (`folder-save`, Spec 05b, „Stammdaten eines
 * Ordners"). */
export function useRenamePrivateArea() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ entityId, body, ifMatch }: RenamePrivateAreaArgs) =>
      request<ObjectHead>(`/entities/private-areas/${entityId}`, {
        method: 'PATCH',
        body,
        idempotencyKey: crypto.randomUUID(),
        ifMatch,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}
